import { useSessionUser } from '@contexts/SessionUserContext';
import { useQuery } from '@tanstack/react-query';
import Modal from 'antd/es/modal';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { WhoamiResponseSchema } from '@mai/types';

import { apiClient } from '@queries/axios';
import { logger } from '@utils/logger';

interface WhoamiResponse {
  userId: string;
}

const fetchWhoami = async (): Promise<WhoamiResponse> => {
  const response = await apiClient.get<WhoamiResponse>(
    '/authentication/whoami',
  );
  const validatedData = WhoamiResponseSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate whoami data', {
      error: validatedData.error,
    });
    return response.data;
  }
  return validatedData.data;
};

const Whoami = () => {
  const {
    userId: persistedUserId,
    updateUserId,
    setInitialQueryFinished,
  } = useSessionUser();
  const [isRedirectToLoginVisible, setIsRedirectToLoginVisible] =
    useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const whoamiQuery = useQuery({
    queryKey: ['whoami'],
    queryFn: fetchWhoami,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  useEffect(() => {
    // Set initial query finished regardless of success or failure
    if (!whoamiQuery.isLoading) {
      setInitialQueryFinished(true);
      logger.info('Authentication check completed', {
        isAuthenticated: !!whoamiQuery.data?.userId,
        isLoading: whoamiQuery.isLoading,
        hasError: !!whoamiQuery.error,
      });
    }

    if (whoamiQuery.data) {
      setIsRedirectToLoginVisible(false);
      const newUserId = whoamiQuery.data.userId;
      if (newUserId !== persistedUserId) {
        updateUserId(newUserId);
      }
      logger.info('Authenticated as user', {
        userId: newUserId,
      });
    }

    if (
      whoamiQuery.error &&
      whoamiQuery.error instanceof AxiosError &&
      whoamiQuery.error.response?.status === 401
    ) {
      if (persistedUserId) {
        updateUserId(null);
      }
      logger.warn('Not authenticated', {
        error: whoamiQuery.error,
      });
      setIsRedirectToLoginVisible(true);
    }
  }, [
    whoamiQuery.data,
    whoamiQuery.error,
    whoamiQuery.isLoading,
    persistedUserId,
    updateUserId,
    setInitialQueryFinished,
    location.pathname,
  ]);

  const isLocationAuthenticatedPath = location.pathname.startsWith('/team');

  return (
    <Modal
      open={isRedirectToLoginVisible && isLocationAuthenticatedPath}
      title="Authentication Error"
      onOk={() => {
        setIsRedirectToLoginVisible(false);
        navigate('/auth/login');
      }}
      okText="Go to Login"
      okButtonProps={{ style: { width: '100%' } }}
      cancelButtonProps={{ style: { display: 'none' } }}
      closable={false}
      width={400}
    >
      <p>You are not currently authenticated, please login and try again.</p>
    </Modal>
  );
};

export default Whoami;
